import { Wand2 } from 'lucide-react';
import type {
  Aggregation,
  CustomFieldSchema,
  CustomModelSchema,
  CustomQuery,
  DateInterval,
  DomainRule,
} from '@contracts';
import { DomainBuilder } from '@/components/DomainBuilder';
import { SavedMetricsBar } from '@/components/SavedMetricsBar';
import { Field, Section, SELECT } from '@/components/wizard/WizardUI';
import { AGGS, INTERVALS } from '@/components/wizard/wizardMeta';

/** Paso "Datos" en modo personalizado: el builder de métrica (modelo/cálculo/agrupar/filtros). */
export function CustomMetricStep({
  schema,
  modelSchema,
  numericFields,
  cModel,
  cAgg,
  cField,
  cGroupBy,
  cInterval,
  cDomain,
  cMasterRules,
  usePeriod,
  currentQuery,
  isDateField,
  onBack,
  onApplyTemplate,
  onPickModel,
  onPickAgg,
  onPickField,
  onPickGroup,
  onPickInterval,
  onDomainChange,
  onToggleMasterRule,
  onUsePeriodChange,
}: {
  schema: CustomModelSchema[];
  modelSchema: CustomModelSchema | undefined;
  numericFields: CustomFieldSchema[];
  cModel: string;
  cAgg: Aggregation;
  cField: string;
  cGroupBy: string;
  cInterval: DateInterval | '';
  cDomain: DomainRule[];
  cMasterRules: string[];
  usePeriod: boolean;
  currentQuery: CustomQuery | null;
  isDateField: (name: string) => boolean;
  onBack: () => void;
  onApplyTemplate: (q: CustomQuery) => void;
  onPickModel: (model: string) => void;
  onPickAgg: (a: Aggregation) => void;
  onPickField: (f: string) => void;
  onPickGroup: (g: string) => void;
  onPickInterval: (iv: DateInterval | '') => void;
  onDomainChange: (d: DomainRule[]) => void;
  onToggleMasterRule: (id: string) => void;
  onUsePeriodChange: (v: boolean) => void;
}) {
  return (
    <Section icon={<Wand2 className="h-4 w-4 text-red-600" />} title="Métrica personalizada">
      <button onClick={onBack} className="mb-3 text-xs font-semibold text-slate-400 hover:text-slate-700">
        ← Volver al catálogo
      </button>

      <SavedMetricsBar current={currentQuery} onLoad={onApplyTemplate} />

      <Field label="Modelo (fuente de datos)">
        <select value={cModel} onChange={(e) => onPickModel(e.target.value)} className={SELECT}>
          <option value="">Elige un modelo…</option>
          {schema.map((s) => (
            <option key={s.model} value={s.model}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>

      {cModel && (
        <>
          <Field label="Cálculo">
            <div className="flex gap-2">
              {AGGS.map((a) => (
                <button
                  key={a.value}
                  onClick={() => onPickAgg(a.value)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                    cAgg === a.value ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </Field>

          {cAgg !== 'count' && (
            <Field label="Campo a calcular (numérico)">
              <select value={cField} onChange={(e) => onPickField(e.target.value)} className={SELECT}>
                <option value="">Elige un campo…</option>
                {numericFields.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Agrupar por (opcional → ranking / serie por fecha)">
            <select value={cGroupBy} onChange={(e) => onPickGroup(e.target.value)} className={SELECT}>
              <option value="">— sin agrupar —</option>
              {modelSchema?.fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.label}
                  {isDateField(f.name) ? ' (fecha)' : ''}
                </option>
              ))}
            </select>
          </Field>

          {cGroupBy && isDateField(cGroupBy) && (
            <Field label="Granularidad de la serie">
              <div className="flex flex-wrap gap-2">
                {INTERVALS.map((iv) => (
                  <button
                    key={iv.value}
                    onClick={() => onPickInterval(iv.value)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                      cInterval === iv.value ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {iv.label}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <Field label="Filtros">
            <DomainBuilder fields={modelSchema?.fields ?? []} domain={cDomain} onChange={onDomainChange} />
          </Field>

          {!!modelSchema?.masterRules?.length && (
            <Field label="Reglas maestras (lógica de negocio del POS)">
              <div className="space-y-1.5">
                {modelSchema.masterRules.map((r) => (
                  <label
                    key={r.id}
                    className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 p-2.5 hover:border-red-300 has-[:checked]:border-red-400 has-[:checked]:bg-red-50/60"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-red-600"
                      checked={cMasterRules.includes(r.id)}
                      onChange={() => onToggleMasterRule(r.id)}
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-700">{r.label}</span>
                      <span className="block text-xs text-slate-500">{r.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
          )}

          {!!modelSchema?.dateFields.length && (
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-500">
              <input
                type="checkbox"
                className="accent-red-600"
                checked={usePeriod}
                onChange={(e) => onUsePeriodChange(e.target.checked)}
              />
              Filtrar por el periodo del dashboard
            </label>
          )}
        </>
      )}
    </Section>
  );
}
